import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, alpha, useTheme, CircularProgress,
  Divider, Chip
} from '@mui/material';
import {
  SwapHoriz, TrendingUp, CalendarMonth, Numbers
} from '@mui/icons-material';
import { format } from 'date-fns';

import { Enrollment, Session, SessionClass, SectionInstance } from '../../types/sessions';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import StatusChip from '../common/StatusChip';

interface AcademicHistoryTimelineProps {
  studentId: string;
  /** Bump this to force a refetch. */
  refreshKey?: number;
}

interface SessionGroup {
  sessionId: string;
  enrollments: Enrollment[];
}

const AcademicHistoryTimeline: React.FC<AcademicHistoryTimelineProps> = ({ studentId, refreshKey }) => {
  const theme = useTheme();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [sessionMap, setSessionMap]   = useState<Map<string, Session>>(new Map());
  const [classMap, setClassMap]       = useState<Map<string, SessionClass>>(new Map());
  const [sectionMap, setSectionMap]   = useState<Map<string, SectionInstance>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await enrollmentsApi.listForStudent(studentId);
        if (cancelled) return;
        setEnrollments(list);

        const sessionIds = Array.from(new Set(list.map(e => String(e.sessionId))));
        const classes = await classesApi.list();
        if (cancelled) return;

        const sessions = await Promise.all(sessionIds.map(id => sessionsApi.get(id).catch(() => null)));
        if (cancelled) return;
        const sMap = new Map<string, Session>();
        sessions.forEach((s, i) => { if (s) sMap.set(sessionIds[i], s); });
        setSessionMap(sMap);

        const cMap = new Map<string, SessionClass>();
        classes.forEach(c => cMap.set(String(c._id), c));
        setClassMap(cMap);

        // Sections — dedupe lookups by (sessionId, classId) so we don't fetch the same slot twice.
        const slotKeys = new Set<string>();
        list.forEach(e => {
          if (e.sectionInstanceId) slotKeys.add(`${String(e.sessionId)}::${String(e.classId)}`);
        });
        if (slotKeys.size > 0) {
          const slots = Array.from(slotKeys).map(k => {
            const [sessionId, classId] = k.split('::');
            return { sessionId, classId };
          });
          const results = await Promise.all(slots.map(s =>
            sectionInstancesApi.list({ sessionId: s.sessionId, classId: s.classId }).catch(() => [])
          ));
          const secMap = new Map<string, SectionInstance>();
          results.flat().forEach(s => secMap.set(String(s._id), s));
          setSectionMap(secMap);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, refreshKey]);

  const sessionGroups = useMemo<SessionGroup[]>(() => {
    const map = new Map<string, Enrollment[]>();
    for (const e of enrollments) {
      const k = String(e.sessionId);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    map.forEach(arr =>
      arr.sort((a, b) => new Date(a.enrolledOn).getTime() - new Date(b.enrolledOn).getTime())
    );
    const groups: SessionGroup[] = Array.from(map.entries()).map(([sessionId, ens]) => ({ sessionId, enrollments: ens }));
    groups.sort((a, b) => {
      const sa = sessionMap.get(a.sessionId);
      const sb = sessionMap.get(b.sessionId);
      const da = sa ? new Date(sa.startDate).getTime() : 0;
      const db = sb ? new Date(sb.startDate).getTime() : 0;
      return db - da;
    });
    return groups;
  }, [enrollments, sessionMap]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 5 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (enrollments.length === 0) {
    return (
      <Box sx={{
        py: 4, px: 2, textAlign: 'center',
        border: `1px dashed ${theme.palette.divider}`, borderRadius: 1.5
      }}>
        <CalendarMonth sx={{ fontSize: 22, color: 'text.disabled', mb: 0.75 }} />
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>No enrollment history yet</Typography>
        <Typography variant="caption" color="text.secondary">
          This student hasn't been enrolled in any session.
        </Typography>
      </Box>
    );
  }

  const enrollmentById = new Map(enrollments.map(e => [String(e._id), e]));

  const renderEnrollment = (e: Enrollment, isLastInGroup: boolean) => {
    const cls = classMap.get(String(e.classId));
    const sec = e.sectionInstanceId ? sectionMap.get(String(e.sectionInstanceId)) : null;
    const isCurrent = e.enrollmentStatus === 'enrolled';
    const promotedFrom = e.promotedFromEnrollmentId ? enrollmentById.get(String(e.promotedFromEnrollmentId)) : null;
    const transferredFrom = e.transferredFromEnrollmentId ? enrollmentById.get(String(e.transferredFromEnrollmentId)) : null;

    // Single subtle accent dot per row — colored by outcome. No boxes, no shadows.
    const dotColor =
      isCurrent                                                                          ? theme.palette.success.main :
      e.enrollmentStatus === 'promoted' || e.enrollmentStatus === 'graduated'            ? theme.palette.success.main :
      e.enrollmentStatus === 'retained'                                                  ? theme.palette.warning.main :
      e.enrollmentStatus === 'inconclusive'                                              ? theme.palette.text.disabled :
                                                                                          theme.palette.error.light;

    return (
      <Box key={String(e._id)} sx={{ position: 'relative', pl: 3 }}>
        {/* Connector line down to the next row in this session group */}
        {!isLastInGroup && (
          <Box sx={{
            position: 'absolute', left: 7, top: 18, bottom: -8, width: '1px',
            bgcolor: theme.palette.divider
          }} />
        )}
        {/* Marker */}
        <Box sx={{
          position: 'absolute', left: 4, top: 8,
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: dotColor,
          boxShadow: isCurrent ? `0 0 0 3px ${alpha(dotColor, 0.18)}` : 'none'
        }} />

        <Box sx={{ pb: isLastInGroup ? 0 : 2.25 }}>
          {/* Title row — class + section + roll, with status chip on the right */}
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 0.25 }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {cls?.name ?? 'Class'}{sec ? ` · Section ${sec.name}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Roll {e.rollNumber}
              </Typography>
            </Stack>
            <StatusChip
              status={{ kind: 'enrollment', value: e.enrollmentStatus }}
              showIcon={false}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>

          {/* Date range — single quiet line */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {format(new Date(e.enrolledOn), 'MMM d, yyyy')}
            {e.exitedOn && <> → {format(new Date(e.exitedOn), 'MMM d, yyyy')}</>}
          </Typography>

          {/* Lineage hints — one line each, inline icon, subtle */}
          {transferredFrom && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
              <SwapHoriz sx={{ fontSize: 13, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">
                Moved from {classMap.get(String(transferredFrom.classId))?.name}
                {transferredFrom.sectionInstanceId ? ` – ${sectionMap.get(String(transferredFrom.sectionInstanceId))?.name ?? ''}` : ''}
              </Typography>
            </Stack>
          )}
          {promotedFrom && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
              <TrendingUp sx={{ fontSize: 13, color: 'success.main' }} />
              <Typography variant="caption" color="text.secondary">
                Promoted from {sessionMap.get(String(promotedFrom.sessionId))?.name} · {classMap.get(String(promotedFrom.classId))?.name}
              </Typography>
            </Stack>
          )}

          {/* Roll-number audit trail — chronological, one line each. Kept
              visually quiet so lifecycle events (moved / promoted) stay the
              headlines. Skipped when the enrollment has no rollHistory. */}
          {(e.rollHistory ?? []).map((h, idx) => (
            <Stack key={`${e._id}-roll-${idx}`} direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Numbers sx={{ fontSize: 13, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">
                Roll updated {h.from} → {h.to} · {format(new Date(h.at), 'MMM d, yyyy')}
              </Typography>
            </Stack>
          ))}

          {/* Exit reason — only when present */}
          {e.exitReason && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              {e.exitReason}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {sessionGroups.map((group, groupIdx) => {
        const session = sessionMap.get(group.sessionId);
        const isLastGroup = groupIdx === sessionGroups.length - 1;
        return (
          <Box key={group.sessionId} sx={{ mb: isLastGroup ? 0 : 2.5 }}>
            {/* Session header — quiet pill aligned with a soft divider line */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
              <Chip
                label={session?.name ?? 'Session'}
                size="small"
                variant="outlined"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 600,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  borderColor: theme.palette.divider
                }}
              />
              {session && (
                <Typography variant="caption" color="text.disabled">
                  {format(new Date(session.startDate), 'MMM yyyy')} – {format(new Date(session.endDate), 'MMM yyyy')}
                </Typography>
              )}
              <Divider sx={{ flex: 1, ml: 0.5 }} />
            </Stack>

            <Box>
              {group.enrollments.map((e, idx) =>
                renderEnrollment(e, idx === group.enrollments.length - 1)
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default AcademicHistoryTimeline;
